import { Component, inject, input, OnInit, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { IBizEntity } from '@optima-stride-ai/core';

@Component({
    selector: 'app-rename-dialogue',
    imports: [CommonModule, ReactiveFormsModule],
    template: `
      <div class="rename-dialog">
        <div class="rename-dialog-content">
          <p class="rename-label">Please enter a new name for the item:</p>
          <input 
            type="text" 
            class="rename-input"
            [formControl]="nameControl"
            [placeholder]="originalName()"
            (keydown.enter)="onEnterPressed()"
            autofocus
          />
          @if (nameControl.invalid && nameControl.touched) {
            <div class="error-message">
              @if (nameControl.hasError('required')) {
                <span>Name is required</span>
              }
              @if (nameControl.hasError('whitespace')) {
                <span>Name cannot be only whitespace</span>
              }
            </div>
          }
        </div>
      </div>
    `,
    styles: `
.rename-dialog
  padding: 0
  font-family: 'Google Sans', Roboto, Arial, sans-serif

.rename-dialog-content
  display: flex
  flex-direction: column
  gap: 12px

.rename-label
  margin: 0
  font-size: 14px
  font-weight: 400
  color: #202124
  line-height: 20px

.rename-input
  width: 100%
  padding: 8px 12px
  font-size: 14px
  line-height: 20px
  color: #202124
  border: 1px solid #dadce0
  border-radius: 4px
  outline: none
  transition: all 0.2s
  box-sizing: border-box
  font-family: 'Google Sans', Roboto, Arial, sans-serif

  &:hover
    border-color: #bdc1c6

  &:focus
    border-color: #1a73e8
    border-width: 2px
    padding: 7px 11px

  &::placeholder
    color: #5f6368

.error-message
  font-size: 12px
  color: #d93025
  margin-top: -8px
    `
})
export class RenameDialogueComponent implements OnInit {
  bizEntity = input<IBizEntity | undefined>();
  message = input<string>('');
  
  originalName = signal<string>('');
  nameControl = new FormControl('', [
    Validators.required,
    this.noWhitespaceValidator.bind(this)
  ]);

  ngOnInit(): void {
    const entity = this.bizEntity();
    const name = entity?.name || '';
    this.originalName.set(name);
    this.nameControl.setValue(name);
    
    // Select the name without extension when input is focused
    setTimeout(() => {
      const input = document.querySelector('.rename-input') as HTMLInputElement;
      if (input && name) {
        const lastDotIndex = name.lastIndexOf('.');
        if (lastDotIndex > 0) {
          input.setSelectionRange(0, lastDotIndex);
        } else {
          input.select();
        }
      }
    }, 100);
  }

  noWhitespaceValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value || '';
    const isWhitespace = value.trim().length === 0;
    return !isWhitespace ? null : { whitespace: true };
  }

  onEnterPressed(): void {
    if (this.nameControl.valid) {
      // This will be handled by the modal's OK button
      const okButton = document.querySelector('[data-rename-ok-btn]') as HTMLButtonElement;
      okButton?.click();
    }
  }

  get data(): { isValid: boolean; newName: string; hasChanged: boolean } {
    const newName = this.nameControl.value?.trim() || '';
    const hasChanged = newName !== this.originalName();
    return {
      isValid: this.nameControl.valid && hasChanged,
      newName,
      hasChanged
    };
  }
}
